import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, CheckCircle, ArrowLeft, Building2, Mail, Phone, User, MessageSquare } from "lucide-react";
import axios from "axios";
import { useTranslation } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-sciad.png";

const DemoPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preselectedSoftware = searchParams.get("logiciel") || "";
  
  const [software, setSoftware] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", software_interest: preselectedSoftware, message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSoftware = async () => {
      try {
        const res = await axios.get(`${API}/software`);
        setSoftware(res.data);
        if (preselectedSoftware && res.data.find(s => s.name === preselectedSoftware)) {
          setForm(prev => ({ ...prev, software_interest: preselectedSoftware }));
        }
      } catch (err) { console.error(err); }
    };
    fetchSoftware();
  }, [preselectedSoftware]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", software_interest: "", message: "" });
    } catch (err) { setStatus("error"); }
    setLoading(false);
  };

  const sidebarItems = t("demo_sidebar_items");

  return (
    <div className="min-h-screen bg-[#0B1121]" data-testid="demo-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SCIAD" className="h-12 w-auto" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" />{t("demo_back")}
          </Link>
        </div>
      </header>

      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {status === "success" ? (
            <div className="text-center py-16" data-testid="demo-success">
              <div className="inline-flex p-6 rounded-full bg-green-500/20 mb-6">
                <CheckCircle className="w-16 h-16 text-green-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-4"><span className="gradient-text">{t("demo_success_title")}</span></h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg mx-auto">{t("demo_success_msg")}</p>
              <Link to="/" className="inline-block px-8 py-4 btn-primary rounded-full text-lg">{t("demo_back")}</Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <p className="text-sm uppercase tracking-[0.3em] text-[#00CCC2] mb-4">{t("demo_label")}</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  <span className="gradient-text">{t("demo_title")}</span>
                </h1>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">{t("demo_subtitle")}</p>
              </div>

              <div className="grid lg:grid-cols-5 gap-12">
                <div className="lg:col-span-3">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="demo-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2"><User className="w-4 h-4 inline mr-2 text-[#00CCC2]" />{t("demo_name")} *</label>
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Jean Dupont"
                          className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="demo-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2"><Building2 className="w-4 h-4 inline mr-2 text-[#00CCC2]" />{t("demo_company")} *</label>
                        <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required
                          className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="demo-company" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2"><Mail className="w-4 h-4 inline mr-2 text-[#00CCC2]" />{t("demo_email")} *</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                          className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="demo-email" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2"><Phone className="w-4 h-4 inline mr-2 text-[#00CCC2]" />{t("demo_phone")} *</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
                          className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors" data-testid="demo-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">{t("demo_software_label")} *</label>
                      <select value={form.software_interest} onChange={(e) => setForm({ ...form, software_interest: e.target.value })} required
                        className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors appearance-none cursor-pointer" data-testid="demo-software">
                        <option value="">{t("demo_software_placeholder")}</option>
                        {software.map((sw) => <option key={sw.id} value={sw.name}>{sw.name}</option>)}
                        <option value="Plusieurs solutions">{t("demo_software_multi")}</option>
                        <option value="Je ne sais pas encore">{t("demo_software_unknown")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2"><MessageSquare className="w-4 h-4 inline mr-2 text-[#00CCC2]" />{t("demo_message")}</label>
                      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                        placeholder={t("demo_message_placeholder")}
                        className="w-full px-5 py-4 rounded-xl bg-[#131B2F] border border-white/10 focus:border-[#2395E3] outline-none transition-colors resize-none" data-testid="demo-message" />
                    </div>
                    {status === "error" && <p className="text-red-400 text-center">{t("contact_error")}</p>}
                    <button type="submit" disabled={loading}
                      className="w-full px-8 py-5 btn-primary rounded-xl flex items-center justify-center gap-3 text-lg font-semibold" data-testid="demo-submit">
                      {loading ? t("demo_submitting") : <><Send className="w-5 h-5" /> {t("demo_submit")}</>}
                    </button>
                    <p className="text-sm text-slate-500 text-center">{t("demo_form_consent")}</p>
                  </form>
                </div>

                <div className="lg:col-span-2">
                  <div className="sticky top-32 space-y-6">
                    <div className="p-6 rounded-2xl bg-[#131B2F] border border-white/10">
                      <h3 className="text-lg font-semibold mb-4">{t("demo_sidebar_title")}</h3>
                      <ul className="space-y-3">
                        {(Array.isArray(sidebarItems) ? sidebarItems : []).map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-[#00CCC2] mt-0.5 flex-shrink-0" />
                            <span className="text-slate-300">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-6 rounded-2xl bg-gradient-to-br from-[#00CCC2]/10 to-[#2395E3]/10 border border-[#00CCC2]/20">
                      <p className="text-sm text-[#00CCC2] font-medium mb-2">{t("demo_sidebar_exp")}</p>
                      <p className="text-slate-300">{t("demo_sidebar_exp_desc")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="py-8 bg-[#0B1121] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} {t("footer_copyright")}
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
